import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, FolderPlus, Search } from 'lucide-react';
import { Category } from '../../../types/index.ts';
import { CategoryIcon } from '../../../pages/Inventory.tsx';
import { spinIconOut } from '../../../utils/iconAnimations.ts';
import { resolveCategoryColor } from './useCategoryColors.ts';

// ─── Left panel: colored tree, browse only ("Categories B" Part 3, 2026-10-11) ─────────────────
export function CategoryTree({
  categories,
  selectedId,
  onSelect,
  onCreateTop,
  categoryColorMap,
  readOnly,
  expandedIds,
  setExpandedIds,
}: {
  categories: Category[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onCreateTop: () => void;
  categoryColorMap: Map<number, string>;
  readOnly: boolean;
  expandedIds: Set<number>;
  setExpandedIds: React.Dispatch<React.SetStateAction<Set<number>>>;
}) {
  const [search, setSearch] = useState('');

  // Matches at every depth, not just top-level names -- and auto-expand every ancestor of a
  // match so a hit three levels down is actually visible without the user manually drilling in.
  const matchingIds = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.trim().toLowerCase();
    return new Set(categories.filter((c) => c.name.toLowerCase().includes(q)).map((c) => c.id));
  }, [search, categories]);

  useEffect(() => {
    if (!matchingIds) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      for (const id of matchingIds) {
        let cur = categories.find((c) => c.id === id);
        while (cur?.parent_id) {
          next.add(cur.parent_id);
          cur = categories.find((c) => c.id === cur!.parent_id);
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingIds]);

  // A row is visible if it (or a descendant) matches the search, or there's no search at all.
  function isVisible(cat: Category): boolean {
    if (!matchingIds) return true;
    if (matchingIds.has(cat.id)) return true;
    return categories.filter((c) => c.parent_id === cat.id).some(isVisible);
  }

  function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function renderRow(cat: Category, depth: number): React.ReactNode {
    if (!isVisible(cat)) return null;
    const children = categories.filter((c) => c.parent_id === cat.id);
    const hasChildren = children.length > 0;
    const expanded = expandedIds.has(cat.id) || !!matchingIds; // force-open while searching
    const isSelected = selectedId === cat.id;
    const color = resolveCategoryColor(cat, categoryColorMap);
    const tileSize = depth === 0 ? 28 : 24;

    return (
      <div key={cat.id}>
        <div
          onClick={() => onSelect(cat.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, height: 40,
            paddingLeft: 10 + depth * 18, paddingRight: 10,
            cursor: 'pointer', borderRadius: 8, position: 'relative',
            background: isSelected ? 'rgba(59,130,246,.16)' : 'transparent',
            boxShadow: isSelected ? 'inset 0 0 0 1px rgba(59,130,246,.4)' : 'none',
          }}
          onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
          onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
        >
          {/* Vertical guide line for nesting */}
          {depth > 0 && (
            <div style={{ position: 'absolute', left: 10 + (depth - 1) * 18 + 9, top: 0, bottom: 0, width: 1, background: 'var(--border-subtle)' }} />
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); if (hasChildren) toggleExpand(cat.id); }}
            style={{
              width: 16, height: 16, flexShrink: 0, background: 'none', border: 'none',
              cursor: hasChildren ? 'pointer' : 'default', color: 'var(--text-muted)', padding: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              visibility: hasChildren ? 'visible' : 'hidden',
            }}
          >
            <ChevronRight size={13} className="cat-tree-chevron" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }} />
          </button>
          <div style={{
            width: tileSize, height: tileSize, borderRadius: 7, flexShrink: 0,
            background: `${color}24`, color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CategoryIcon iconName={cat.icon_path} size={tileSize === 28 ? 15 : 13} color="currentColor" />
          </div>
          <span style={{
            flex: 1, fontSize: 13, color: isSelected ? 'var(--accent-primary)' : 'var(--text-primary)',
            fontWeight: isSelected ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {cat.name}
          </span>
          {hasChildren && (
            <span style={{
              fontSize: 10, fontWeight: 600, color: '#60a5fa', background: 'rgba(59,130,246,0.15)',
              borderRadius: 999, padding: '2px 7px', flexShrink: 0, whiteSpace: 'nowrap',
            }}>
              {children.length} sub
            </span>
          )}
        </div>
        {hasChildren && expanded && children.map((child) => renderRow(child, depth + 1))}
      </div>
    );
  }

  const topLevel = categories.filter((c) => !c.parent_id);

  return (
    <div style={{ width: 320, flexShrink: 0, borderRight: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)' }}>
      <div style={{ padding: 12, borderBottom: '1px solid var(--border-subtle)' }}>
        <div className="search-bar search-input-wrapper" style={{ height: 32, boxSizing: 'border-box' }}>
          <Search size={13} color="var(--text-muted)" className="btn-icon" />
          <input
            type="text"
            placeholder="Search all categories"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape' && search) { e.stopPropagation(); setSearch(''); } }}
          />
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 6px' }}>
        {topLevel.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No categories yet</div>
        ) : (
          topLevel.map((cat) => renderRow(cat, 0))
        )}
      </div>
      {!readOnly && (
        <div style={{ padding: 10, borderTop: '1px solid var(--border-subtle)' }}>
          <button
            type="button"
            onClick={onCreateTop}
            onMouseLeave={spinIconOut}
            className="btn btn-ghost btn-sm combo1"
            style={{ width: '100%', border: '1px dashed var(--border-default)', justifyContent: 'center' }}
          >
            <FolderPlus size={14} className="btn-icon" /> New category
          </button>
        </div>
      )}
      <style>{`
        .cat-tree-chevron { transition: transform 0.15s ease; }
        @media (prefers-reduced-motion: reduce) {
          .cat-tree-chevron { transition: none; }
        }
      `}</style>
    </div>
  );
}
